import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Container,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  AssessmentOutlined,
  SecurityOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ErrorOutlineOutlined,
  VisibilityOutlined,
  DownloadOutlined,
  ScheduleOutlined,
  PhotoCameraOutlined,
  TextFieldsOutlined,
  PsychologyOutlined,
  CloseOutlined,
  ShareOutlined,
  StopOutlined,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../store/hooks';
import { bulkAnalysisApi } from '../services/api';

interface QueryStatus {
  query_id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  progress_percentage: number;
  current_step: string;
  total_products: number;
  products_processed: number;
  successful_analyses: number;
  failed_analyses: number;
  counterfeit_count: number;
  authentic_count: number;
  suspicious_count: number;
  success_rate: number;
  counterfeit_rate: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  celery_task_id?: string;
}

const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
}));


interface AnalysisResult {
  id: string;
  testProduct: {
    name: string;
    brand: string;
    category: string;
    url?: string;
    image?: string;
  };
  referenceProduct?: {
    id: string;
    name: string;
    brand: string;
    category: string;
    image?: string;
    confidence: number;
  };
  analysis: {
    is_counterfeit: boolean;
    confidence: number;
    risk_level: 'low' | 'medium' | 'high';
    risk_score: number;
    image_similarity_score: number;
    text_similarity_score: number;
    llm_analysis_score: number;
    key_differences: string[];
    recommendations: string[];
    detailed_analysis: {
      total_analyses: number;
      successful_analyses: number;
      failed_analyses: number;
      processing_status: string;
      error_message?: string;
    };
  };
  processingTime: number;
  analysisDate: string;
}

interface BulkAnalysisSummary {
  totalProducts: number;
  counterfeitCount: number;
  authenticCount: number;
  suspiciousCount: number;
  averageConfidence: number;
  averageProcessingTime: number;
  analysisDate: string;
  queryType: 'url' | 'csv';
  queryContent: string;
}

const BulkAnalysisResultsPage: React.FC = () => {
  const { queryId } = useParams<{ queryId: string }>();
  const { loading, error } = useAppSelector((state) => state.analyses);
  
  // State
  const [results, setResults] = useState<AnalysisResult[]>([]);
  const [summary, setSummary] = useState<BulkAnalysisSummary | null>(null);
  const [selectedResult, setSelectedResult] = useState<AnalysisResult | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'counterfeit' | 'authentic' | 'suspicious'>('all');
  const [sortBy] = useState<'confidence' | 'risk_score' | 'date'>('confidence');
  const [sortOrder] = useState<'asc' | 'desc'>('desc');
  const [queryStatus, setQueryStatus] = useState<QueryStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Load actual bulk analysis results
  useEffect(() => {
    const loadResults = async () => {
      if (!queryId) return;
      
      try {
        // First check the status
        const statusResponse = await bulkAnalysisApi.getStatus(queryId);
        setQueryStatus(statusResponse);
        
        // If completed, get results
        if (statusResponse.status === 'COMPLETED') {
          const data = await bulkAnalysisApi.getResults(queryId);
          setSummary(data.summary);
          setResults(data.results);
        } else if (statusResponse.status === 'PROCESSING' || statusResponse.status === 'PENDING') {
          // Start polling for status updates
          setIsPolling(true);
        } else {
          // For cancelled/failed queries, show empty results with status info
          const emptySummary: BulkAnalysisSummary = {
            totalProducts: statusResponse.total_products,
            counterfeitCount: statusResponse.counterfeit_count,
            authenticCount: statusResponse.authentic_count,
            suspiciousCount: statusResponse.suspicious_count,
            averageConfidence: 0,
            averageProcessingTime: 0,
            analysisDate: statusResponse.created_at,
            queryType: 'url', // Default fallback
            queryContent: `Query ID: ${queryId}`,
          };
          setSummary(emptySummary);
          setResults([]);
        }
      } catch (error) {
        console.error('Failed to load bulk analysis results:', error);
        // Fall back to mock data for development
        const mockResults: AnalysisResult[] = Array.from({ length: 25 }, (_, i) => ({
          id: `result-${i}`,
          testProduct: {
            name: `Test Product ${i + 1}`,
            brand: `Brand ${i + 1}`,
            category: ['Electronics', 'Footwear', 'Clothing', 'Accessories'][i % 4],
            url: `https://example.com/product${i + 1}`,
            image: `https://via.placeholder.com/150x150?text=Product+${i + 1}`,
          },
          referenceProduct: {
            id: `ref-${i}`,
            name: `Reference Product ${i + 1}`,
            brand: `Brand ${i + 1}`,
            category: ['Electronics', 'Footwear', 'Clothing', 'Accessories'][i % 4],
            image: `https://via.placeholder.com/150x150?text=Ref+${i + 1}`,
            confidence: 0.7 + Math.random() * 0.3,
          },
          analysis: {
            is_counterfeit: Math.random() > 0.7,
            confidence: 0.6 + Math.random() * 0.4,
            risk_level: Math.random() > 0.7 ? 'high' : Math.random() > 0.4 ? 'medium' : 'low',
            risk_score: Math.random(),
            image_similarity_score: 0.5 + Math.random() * 0.5,
            text_similarity_score: 0.5 + Math.random() * 0.5,
            llm_analysis_score: 0.5 + Math.random() * 0.5,
            key_differences: ['Color variation', 'Logo placement', 'Packaging differences'],
            recommendations: ['Verify with manufacturer', 'Check packaging details', 'Contact supplier'],
            detailed_analysis: {
              total_analyses: 3,
              successful_analyses: 2,
              failed_analyses: 1,
              processing_status: 'completed',
            },
          },
          processingTime: 2 + Math.random() * 3,
          analysisDate: new Date(Date.now() - Math.random() * 86400000).toISOString(),
        }));

        const mockSummary: BulkAnalysisSummary = {
          totalProducts: 25,
          counterfeitCount: mockResults.filter(r => r.analysis.is_counterfeit).length,
          authenticCount: mockResults.filter(r => !r.analysis.is_counterfeit && r.analysis.risk_level === 'low').length,
          suspiciousCount: mockResults.filter(r => !r.analysis.is_counterfeit && r.analysis.risk_level !== 'low').length,
          averageConfidence: mockResults.reduce((sum, r) => sum + r.analysis.confidence, 0) / mockResults.length,
          averageProcessingTime: mockResults.reduce((sum, r) => sum + r.processingTime, 0) / mockResults.length,
          analysisDate: new Date().toISOString(),
          queryType: 'url',
          queryContent: 'https://example.com/product1, https://example.com/product2, https://example.com/product3',
        };

        setResults(mockResults);
        setSummary(mockSummary);
      }
    };

    loadResults();
  }, [queryId]);

  // Polling effect for status updates
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isPolling && queryId) {
      interval = setInterval(async () => {
        try {
          const status = await bulkAnalysisApi.getStatus(queryId);
          setQueryStatus(status);
          
          if (status.status === 'COMPLETED') {
            const data = await bulkAnalysisApi.getResults(queryId);
            setSummary(data.summary);
            setResults(data.results);
            setIsPolling(false);
          } else if (status.status === 'FAILED' || status.status === 'CANCELLED') {
            // Show summary with status info for failed/cancelled queries
            const emptySummary: BulkAnalysisSummary = {
              totalProducts: status.total_products,
              counterfeitCount: status.counterfeit_count,
              authenticCount: status.authentic_count,
              suspiciousCount: status.suspicious_count,
              averageConfidence: 0,
              averageProcessingTime: 0,
              analysisDate: status.created_at,
              queryType: 'url', // Default fallback
              queryContent: `Query ID: ${queryId}`,
            };
            setSummary(emptySummary);
            setResults([]);
            setIsPolling(false);
          }
        } catch (error) {
          console.error('Error polling status:', error);
          setIsPolling(false);
        }
      }, 3000); // Poll every 3 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPolling, queryId]);

  // Cancel analysis handler
  const handleCancelAnalysis = async () => {
    if (!queryId) return;
    
    try {
      await bulkAnalysisApi.cancelQuery(queryId);
      const status = await bulkAnalysisApi.getStatus(queryId);
      setQueryStatus(status);
      setIsPolling(false);
    } catch (error) {
      console.error('Failed to cancel analysis:', error);
    }
  };

  const handleViewDetail = (result: AnalysisResult) => {
    setSelectedResult(result);
    setShowDetailDialog(true);
  };

  const handleCloseDetail = () => {
    setShowDetailDialog(false);
    setSelectedResult(null);
  };


  const getStatusIcon = (result: AnalysisResult) => {
    if (result.analysis.is_counterfeit) return <ErrorOutlineOutlined />;
    if (result.analysis.risk_level === 'high') return <WarningOutlined />;
    return <CheckCircleOutlined />;
  };

  const filteredResults = results.filter(result => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'counterfeit') return result.analysis.is_counterfeit;
    if (filterStatus === 'authentic') return !result.analysis.is_counterfeit && result.analysis.risk_level === 'low';
    if (filterStatus === 'suspicious') return !result.analysis.is_counterfeit && result.analysis.risk_level !== 'low';
    return true;
  });

  const sortedResults = [...filteredResults].sort((a, b) => {
    let aValue: number, bValue: number;
    
    switch (sortBy) {
      case 'confidence':
        aValue = a.analysis.confidence;
        bValue = b.analysis.confidence;
        break;
      case 'risk_score':
        aValue = a.analysis.risk_score;
        bValue = b.analysis.risk_score;
        break;
      case 'date':
        aValue = new Date(a.analysisDate).getTime();
        bValue = new Date(b.analysisDate).getTime();
        break;
      default:
        return 0;
    }
    
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
  });

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Bulk Analysis Results
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Analysis results for query: {summary?.queryContent || queryId}
        </Typography>
        
        {/* Status Display */}
        {queryStatus && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Chip
                icon={queryStatus.status === 'COMPLETED' ? <CheckCircleOutlined /> : 
                      queryStatus.status === 'PROCESSING' ? <CircularProgress size={16} /> :
                      queryStatus.status === 'FAILED' ? <ErrorOutlineOutlined /> :
                      <ScheduleOutlined />}
                label={queryStatus.status}
                color={queryStatus.status === 'COMPLETED' ? 'success' : 
                       queryStatus.status === 'PROCESSING' ? 'warning' :
                       queryStatus.status === 'FAILED' ? 'error' : 'default'}
              />
              <Typography variant="body2" color="text.secondary">
                {queryStatus.current_step && `Current step: ${queryStatus.current_step}`}
              </Typography>
              {(queryStatus.status === 'PROCESSING' || queryStatus.status === 'PENDING') && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<StopOutlined />}
                  onClick={handleCancelAnalysis}
                >
                  Cancel Analysis
                </Button>
              )}
            </Box>
            
            {/* Progress Bar */}
            {queryStatus.status === 'PROCESSING' && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={queryStatus.progress_percentage}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {queryStatus.progress_percentage.toFixed(1)}% complete
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {queryStatus.products_processed} / {queryStatus.total_products} products
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AssessmentOutlined sx={{ mr: 2, color: 'primary.main' }} />
                  <Typography variant="h6">Total Products</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  {summary.totalProducts}
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ErrorOutlineOutlined sx={{ mr: 2, color: 'error.main' }} />
                  <Typography variant="h6">Counterfeit</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {summary.counterfeitCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {((summary.counterfeitCount / summary.totalProducts) * 100).toFixed(1)}% of total
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircleOutlined sx={{ mr: 2, color: 'success.main' }} />
                  <Typography variant="h6">Authentic</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {summary.authenticCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {((summary.authenticCount / summary.totalProducts) * 100).toFixed(1)}% of total
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <WarningOutlined sx={{ mr: 2, color: 'warning.main' }} />
                  <Typography variant="h6">Suspicious</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {summary.suspiciousCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {((summary.suspiciousCount / summary.totalProducts) * 100).toFixed(1)}% of total
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      )}

      {/* Filters and Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          variant={filterStatus === 'all' ? 'contained' : 'outlined'}
          size="small"
          onClick={() => setFilterStatus('all')}
        >
          All ({results.length})
        </Button>
        <Button
          variant={filterStatus === 'counterfeit' ? 'contained' : 'outlined'}
          size="small"
          color="error"
          onClick={() => setFilterStatus('counterfeit')}
        >
          Counterfeit ({results.filter(r => r.analysis.is_counterfeit).length})
        </Button>
        <Button
          variant={filterStatus === 'authentic' ? 'contained' : 'outlined'}
          size="small"
          color="success"
          onClick={() => setFilterStatus('authentic')}
        >
          Authentic ({results.filter(r => !r.analysis.is_counterfeit && r.analysis.risk_level === 'low').length})
        </Button>
        <Button
          variant={filterStatus === 'suspicious' ? 'contained' : 'outlined'}
          size="small"
          color="warning"
          onClick={() => setFilterStatus('suspicious')}
        >
          Suspicious ({results.filter(r => !r.analysis.is_counterfeit && r.analysis.risk_level !== 'low').length})
        </Button>
        
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadOutlined />}
          >
            Export Results
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ShareOutlined />}
          >
            Share Report
          </Button>
        </Box>
      </Box>

      {/* Results Table */}
      <StyledCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssessmentOutlined sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6" component="h2">
              Analysis Results ({filteredResults.length})
            </Typography>
          </Box>
          
          {/* Show message for non-completed queries */}
          {queryStatus && queryStatus.status !== 'COMPLETED' && (
            <Alert 
              severity={queryStatus.status === 'CANCELLED' ? 'warning' : 'error'} 
              sx={{ mb: 3 }}
            >
              <Typography variant="body2">
                {queryStatus.status === 'CANCELLED' 
                  ? 'This analysis was cancelled. You can see the query details above.' 
                  : queryStatus.status === 'FAILED'
                  ? `Analysis failed: ${queryStatus.error_message || 'Unknown error occurred'}`
                  : queryStatus.status === 'PROCESSING'
                  ? 'Analysis is still in progress. Results will appear here when completed.'
                  : 'Analysis is pending. Please wait for it to start.'}
              </Typography>
            </Alert>
          )}
          
          {filteredResults.length > 0 ? (
            <TableContainer>
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Test Product</TableCell>
                  <TableCell>Reference Product</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Risk Level</TableCell>
                  <TableCell>Processing Time</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedResults.map((result) => (
                  <TableRow key={result.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={result.testProduct.image}
                          sx={{ width: 40, height: 40 }}
                        >
                          {result.testProduct.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {result.testProduct.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {result.testProduct.brand} • {result.testProduct.category}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {result.referenceProduct ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={result.referenceProduct.image}
                            sx={{ width: 40, height: 40 }}
                          >
                            {result.referenceProduct.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {result.referenceProduct.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Match: {(result.referenceProduct.confidence * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No reference match
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(result)}
                        label={result.analysis.is_counterfeit ? 'Counterfeit' : result.analysis.risk_level === 'high' ? 'Suspicious' : 'Authentic'}
                        color={result.analysis.is_counterfeit ? 'error' : result.analysis.risk_level === 'high' ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">
                          {(result.analysis.confidence * 100).toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={result.analysis.confidence * 100}
                          sx={{ width: 60, height: 6, borderRadius: 3 }}
                          color={result.analysis.confidence > 0.8 ? 'success' : result.analysis.confidence > 0.6 ? 'warning' : 'error'}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={result.analysis.risk_level}
                        color={result.analysis.risk_level === 'high' ? 'error' : result.analysis.risk_level === 'medium' ? 'warning' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {result.processingTime.toFixed(1)}s
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityOutlined />}
                        onClick={() => handleViewDetail(result)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {queryStatus?.status === 'COMPLETED' 
                  ? 'No analysis results available for this query.'
                  : 'Analysis results will appear here once the query is completed.'}
              </Typography>
            </Box>
          )}
        </CardContent>
      </StyledCard>

      {/* Detail Dialog */}
      <Dialog
        open={showDetailDialog}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        {selectedResult && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                  Detailed Analysis: {selectedResult.testProduct.name}
                </Typography>
                <IconButton onClick={handleCloseDetail}>
                  <CloseOutlined />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Product Comparison */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Product Comparison
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            Test Product
                          </Typography>
                          <Typography variant="h6">
                            {selectedResult.testProduct.name}
                          </Typography>
                          <Typography variant="body2">
                            {selectedResult.testProduct.brand} • {selectedResult.testProduct.category}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            Reference Product
                          </Typography>
                          {selectedResult.referenceProduct ? (
                            <>
                              <Typography variant="h6">
                                {selectedResult.referenceProduct.name}
                              </Typography>
                              <Typography variant="body2">
                                {selectedResult.referenceProduct.brand} • {selectedResult.referenceProduct.category}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No reference product matched
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Analysis Scores */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Analysis Scores
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <PhotoCameraOutlined sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h6">
                          {(selectedResult.analysis.image_similarity_score * 100).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Image Similarity
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <TextFieldsOutlined sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h6">
                          {(selectedResult.analysis.text_similarity_score * 100).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Text Similarity
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: 'center', p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <PsychologyOutlined sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h6">
                          {(selectedResult.analysis.llm_analysis_score * 100).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          LLM Analysis
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Key Differences */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Key Differences
                  </Typography>
                  <List>
                    {selectedResult.analysis.key_differences.map((difference, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <WarningOutlined color="warning" />
                        </ListItemIcon>
                        <ListItemText primary={difference} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>

                {/* Recommendations */}
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Recommendations
                  </Typography>
                  <List>
                    {selectedResult.analysis.recommendations.map((recommendation, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          <SecurityOutlined color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={recommendation} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetail}>Close</Button>
              <Button variant="contained" startIcon={<DownloadOutlined />}>
                Export Report
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
    </Container>
  );
};

export default BulkAnalysisResultsPage; 